import React from 'react';

/**
 * Tiny dependency-free SVG sparkline.
 *
 * Why no charting library? For a 30-point trendline, importing recharts/chart.js
 * (~80-150 KB) is wasteful. SVG polyline is exactly what we need and renders
 * pixel-perfect at any size.
 *
 * Props:
 *   data: array of { date: 'YYYY-MM-DD', count: number }
 *   width, height: viewBox dimensions in px
 *   color: stroke + fill tint
 */
const Sparkline = ({ data = [], width = 600, height = 80, color = '#C5A059' }) => {
  if (!data.length) return null;

  const max = Math.max(1, ...data.map(d => d.count));
  const padX = 4;
  const padY = 6;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const points = data.map((d, i) => {
    const x = padX + (i / Math.max(1, data.length - 1)) * innerW;
    // Invert Y so larger values are higher on screen
    const y = padY + innerH - (d.count / max) * innerH;
    return { x, y, ...d };
  });

  const polyline = points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  // Area underneath the line
  const area = [
    `${padX},${height - padY}`,
    polyline,
    `${width - padX},${height - padY}`,
  ].join(' ');

  // Show ~5 evenly-spaced x-axis labels (start, +25%, mid, +75%, end)
  const labelIdxs = [0, Math.floor(data.length * 0.25), Math.floor(data.length * 0.5), Math.floor(data.length * 0.75), data.length - 1];
  const fmtLabel = (iso) => {
    const dt = new Date(iso);
    return `${dt.getDate()}/${dt.getMonth() + 1}`;
  };

  const last = data[data.length - 1];
  const peak = data.reduce((m, d) => (d.count > m.count ? d : m), data[0]);

  return (
    <div style={styles.wrapper} data-testid="sparkline">
      <svg
        viewBox={`0 0 ${width} ${height + 18}`}
        preserveAspectRatio="none"
        style={styles.svg}
      >
        {/* Baseline */}
        <line
          x1={padX}
          y1={height - padY}
          x2={width - padX}
          y2={height - padY}
          stroke="#EAE5DD"
          strokeWidth="1"
        />
        {/* Area under curve */}
        <polygon points={area} fill={color} fillOpacity="0.12" />
        {/* Trend line */}
        <polyline
          points={polyline}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Last + peak dot markers */}
        <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3.5" fill={color} />
        {peak.count > 0 && peak !== last && (
          <circle cx={points[data.indexOf(peak)].x} cy={points[data.indexOf(peak)].y} r="2.5" fill="#1A1918" />
        )}
        {/* X-axis labels */}
        {labelIdxs.map((idx) => (
          <text
            key={idx}
            x={points[idx].x}
            y={height + 14}
            textAnchor="middle"
            style={styles.label}
          >
            {fmtLabel(data[idx].date)}
          </text>
        ))}
      </svg>
      <div style={styles.legend}>
        <span style={styles.legendItem}>
          <span style={{ ...styles.legendDot, backgroundColor: color }} />
          Vandaag: <strong style={styles.legendValue}>{last.count}</strong>
        </span>
        {peak.count > 0 && (
          <span style={styles.legendItem}>
            <span style={{ ...styles.legendDot, backgroundColor: '#1A1918' }} />
            Piek: <strong style={styles.legendValue}>{peak.count}</strong> op {fmtLabel(peak.date)}
          </span>
        )}
        <span style={styles.legendItem}>
          Totaal: <strong style={styles.legendValue}>{data.reduce((s, d) => s + d.count, 0)}</strong> leads
        </span>
      </div>
    </div>
  );
};

const styles = {
  wrapper: { width: '100%' },
  svg: { width: '100%', height: 'auto', display: 'block' },
  label: {
    fontSize: '10px',
    fontFamily: "'Manrope', sans-serif",
    fill: '#8C8B88',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    marginTop: '0.75rem',
    fontSize: '0.78rem',
    color: '#585755',
  },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem' },
  legendDot: {
    width: '8px',
    height: '8px',
    borderRadius: '999px',
    display: 'inline-block',
  },
  legendValue: { color: '#1A1918', fontWeight: 700 },
};

export default Sparkline;
